import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip,
  Menu,
  MenuItem,
  Fab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Star as StarIcon
} from '@mui/icons-material';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const EmailListsPage = () => {
  const { user } = useContext(AuthContext);
  const [emailLists, setEmailLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Form states
  const [listFormData, setListFormData] = useState({
    name: '',
    description: '',
    isDefault: false
  });
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    notes: '',
    tags: ''
  });
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Import states
  const [importData, setImportData] = useState('');
  const [importResults, setImportResults] = useState(null);
  
  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTarget, setMenuTarget] = useState(null);
  
  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    fetchEmailLists();
  }, []);

  const fetchEmailLists = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/email-lists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailLists(response.data);
      
      // Select default list or first list
      const defaultList = response.data.find(list => list.isDefault);
      if (defaultList) {
        setSelectedList(defaultList);
      } else if (response.data.length > 0) {
        setSelectedList(response.data[0]);
      }
      
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load email lists');
      setLoading(false);
    }
  };

  // Email list operations
  const handleCreateList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/email-lists`, listFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fetchEmailLists();
      setListDialogOpen(false);
      resetListForm();
      setSuccess('Email list created successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create email list');
    }
  };

  const handleUpdateList = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/email-lists/${editingItem._id}`, listFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fetchEmailLists();
      setListDialogOpen(false);
      resetListForm();
      setSuccess('Email list updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update email list');
    }
  };

  const handleDeleteList = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/email-lists/${itemToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fetchEmailLists();
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      if (selectedList && selectedList._id === itemToDelete._id) {
        setSelectedList(null);
      }
      setSuccess('Email list deleted successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete email list');
    }
  };

  // Contact operations
  const handleAddContact = async () => {
    try {
      const token = localStorage.getItem('token');
      const contactData = {
        ...contactFormData,
        tags: contactFormData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      };
      
      const response = await axios.post(`${API_URL}/email-lists/${selectedList._id}/contacts`, contactData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedList(response.data);
      await fetchEmailLists();
      setContactDialogOpen(false);
      resetContactForm();
      setSuccess('Contact added successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add contact');
    }
  };

  const handleUpdateContact = async () => {
    try {
      const token = localStorage.getItem('token');
      const contactData = {
        ...contactFormData,
        tags: contactFormData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      };
      
      const response = await axios.put(
        `${API_URL}/email-lists/${selectedList._id}/contacts/${editingItem._id}`, 
        contactData, {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setSelectedList(response.data);
      await fetchEmailLists();
      setContactDialogOpen(false);
      resetContactForm();
      setSuccess('Contact updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update contact');
    }
  };

  const handleDeleteContact = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/email-lists/${selectedList._id}/contacts/${itemToDelete._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setSelectedList(response.data);
      await fetchEmailLists();
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setSuccess('Contact removed successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove contact');
    }
  };

  // Import contacts
  const handleImportContacts = async () => {
    try {
      const lines = importData.trim().split('\n');
      const contacts = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        const parts = line.split(',').map(part => part.trim().replace(/"/g, ''));
        if (parts.length >= 2) {
          contacts.push({
            name: parts[0],
            email: parts[1],
            company: parts[2] || '',
            role: parts[3] || '',
            notes: parts[4] || '',
            tags: parts[5] ? parts[5].split(';').map(tag => tag.trim()) : []
          });
        }
      }
      
      if (contacts.length === 0) {
        setError('No valid contacts found in import data');
        return;
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/email-lists/${selectedList._id}/contacts/import`, 
        { contacts }, {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setImportResults(response.data);
      setSelectedList(response.data.list);
      await fetchEmailLists();
      setImportData('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to import contacts');
    }
  };

  // Helper functions
  const resetListForm = () => {
    setListFormData({ name: '', description: '', isDefault: false });
    setEditingItem(null);
  };

  const resetContactForm = () => {
    setContactFormData({ name: '', email: '', company: '', role: '', notes: '', tags: '' });
    setEditingItem(null);
  };

  const openEditListDialog = (list) => {
    setEditingItem(list);
    setListFormData({
      name: list.name,
      description: list.description || '',
      isDefault: list.isDefault
    });
    setListDialogOpen(true);
  };

  const openEditContactDialog = (contact) => {
    setEditingItem(contact);
    setContactFormData({
      name: contact.name,
      email: contact.email,
      company: contact.company || '',
      role: contact.role || '',
      notes: contact.notes || '',
      tags: contact.tags ? contact.tags.join(', ') : ''
    });
    setContactDialogOpen(true);
  };

  const handleMenuClick = (event, item, type) => {
    setMenuAnchor(event.currentTarget);
    setMenuTarget({ item, type });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTarget(null);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography>Loading email lists...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ color: '#e4e4e4' }}>
          Email Lists
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your promotional email lists and contacts
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Email Lists Sidebar */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">My Lists</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setListDialogOpen(true)}
                size="small"
              >
                New List
              </Button>
            </Box>
            
            <List>
              {emailLists.map((list) => (
                <React.Fragment key={list._id}>
                  <ListItem
                    button
                    selected={selectedList && selectedList._id === list._id}
                    onClick={() => setSelectedList(list)}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {list.name}
                          {list.isDefault && <StarIcon color="primary" fontSize="small" />}
                        </Box>
                      }
                      secondary={`${list.totalContacts} contacts`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, list, 'list')}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {selectedList ? (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h5" gutterBottom>
                    {selectedList.name}
                    {selectedList.isDefault && <StarIcon color="primary" sx={{ ml: 1 }} />}
                  </Typography>
                  {selectedList.description && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedList.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => setImportDialogOpen(true)}
                    size="small"
                  >
                    Import
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setContactDialogOpen(true)}
                  >
                    Add Contact
                  </Button>
                </Box>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Tags</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedList.contacts.filter(contact => contact.isActive).map((contact) => (
                      <TableRow key={contact._id}>
                        <TableCell>{contact.name}</TableCell>
                        <TableCell>{contact.email}</TableCell>
                        <TableCell>{contact.company || '-'}</TableCell>
                        <TableCell>{contact.role || '-'}</TableCell>
                        <TableCell>
                          {contact.tags && contact.tags.length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {contact.tags.map((tag, index) => (
                                <Chip key={index} label={tag} size="small" variant="outlined" />
                              ))}
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => openEditContactDialog(contact)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setItemToDelete(contact);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {selectedList.contacts.filter(contact => contact.isActive).length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <EmailIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No contacts yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add contacts to start building your promotional email list
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setContactDialogOpen(true)}
                  >
                    Add First Contact
                  </Button>
                </Box>
              )}
            </Paper>
          ) : (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Create Your First Email List
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Start building your promotional contacts by creating an email list
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => setListDialogOpen(true)}
              >
                Create Email List
              </Button>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Create/Edit List Dialog */}
      <Dialog open={listDialogOpen} onClose={() => setListDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Email List' : 'Create New Email List'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="List Name"
            value={listFormData.name}
            onChange={(e) => setListFormData({ ...listFormData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={listFormData.description}
            onChange={(e) => setListFormData({ ...listFormData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <FormControlLabel
            control={
              <Switch
                checked={listFormData.isDefault}
                onChange={(e) => setListFormData({ ...listFormData, isDefault: e.target.checked })}
              />
            }
            label="Set as default list"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setListDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={editingItem ? handleUpdateList : handleCreateList}
            variant="contained"
            disabled={!listFormData.name}
          >
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Contact' : 'Add New Contact'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={contactFormData.name}
            onChange={(e) => setContactFormData({ ...contactFormData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={contactFormData.email}
            onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Company"
            value={contactFormData.company}
            onChange={(e) => setContactFormData({ ...contactFormData, company: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Role"
            value={contactFormData.role}
            onChange={(e) => setContactFormData({ ...contactFormData, role: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Tags (comma separated)"
            value={contactFormData.tags}
            onChange={(e) => setContactFormData({ ...contactFormData, tags: e.target.value })}
            margin="normal"
            placeholder="label, radio, blog"
          />
          <TextField
            fullWidth
            label="Notes"
            value={contactFormData.notes}
            onChange={(e) => setContactFormData({ ...contactFormData, notes: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={editingItem ? handleUpdateContact : handleAddContact}
            variant="contained"
            disabled={!contactFormData.name || !contactFormData.email}
          >
            {editingItem ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Contacts Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Contacts</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Import contacts from CSV format. Each line should contain: Name, Email, Company, Role, Notes, Tags (semicolon separated)
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', bgcolor: 'background.paper', p: 1, borderRadius: 1 }}>
            Example:<br/>
            John Doe, john@example.com, Example Corp, A&R, Great contact, label;radio<br/>
            Jane Smith, jane@radio.com, Radio Station, DJ, Plays electronic music, radio;dj
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={importData}
            onChange={(e) => setImportData(e.target.value)}
            placeholder="Paste your CSV data here..."
            margin="normal"
          />
          {importResults && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {importResults.message}
              {importResults.errors.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">First few errors:</Typography>
                  <ul>
                    {importResults.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Close</Button>
          <Button 
            onClick={handleImportContacts}
            variant="contained"
            disabled={!importData.trim()}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {itemToDelete?.name || 'this item'}?
            {itemToDelete && emailLists.find(list => list._id === itemToDelete._id) && ' This will also delete all contacts in this list.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={itemToDelete && emailLists.find(list => list._id === itemToDelete._id) ? handleDeleteList : handleDeleteContact}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuTarget?.type === 'list' && [
          <MenuItem key="edit" onClick={() => {
            openEditListDialog(menuTarget.item);
            handleMenuClose();
          }}>
            <EditIcon sx={{ mr: 1 }} />
            Edit List
          </MenuItem>,
          <MenuItem key="delete" onClick={() => {
            setItemToDelete(menuTarget.item);
            setDeleteDialogOpen(true);
            handleMenuClose();
          }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete List
          </MenuItem>
        ]}
      </Menu>
    </Container>
  );
};

export default EmailListsPage;
